import { ethers } from 'ethers';
import { getState } from '../store/cns';
import { UserRejectedSigningError } from 'src/types/errors';
import { useSignerStore } from 'src/store/signer';

export const BASE_SEPOLIA = {
  chainId: 84532,
  hexChainId: '0x14a34',
  name: 'base-sepolia',
  // drpc allows eth_getLogs ranges up to 10k blocks; sepolia.base.org caps at 2k
  rpcUrl: 'https://base-sepolia.drpc.org',
  explorerUrl: 'https://sepolia.basescan.org',
};

export const getProvider = (): ethers.Provider => {
  return new ethers.JsonRpcProvider(
    BASE_SEPOLIA.rpcUrl,
    {
      chainId: BASE_SEPOLIA.chainId,
      name: BASE_SEPOLIA.name,
    },
    // batchMaxCount: public Base Sepolia RPCs mishandle batched eth_calls.
    // staticNetwork: skip the eth_chainId detection round-trip — a new provider is
    // created per query, and a dropped detection call puts ethers in a retry loop.
    { batchMaxCount: 1, staticNetwork: true },
  );
};

/**
 * Connect the injected wallet (MetaMask), making sure it is on Base Sepolia.
 * Replaces the old raw-private-key login.
 */
export const connectMetaMask = async (): Promise<ethers.JsonRpcSigner> => {
  const eth = (window as any).ethereum;
  if (!eth) {
    throw new Error('MetaMask (or another EVM wallet) is required to connect.');
  }
  await eth.request({ method: 'eth_requestAccounts' });
  try {
    await eth.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: BASE_SEPOLIA.hexChainId }],
    });
  } catch (switchError: any) {
    // 4902 = chain not added to the wallet yet -> add it
    if (switchError?.code === 4902) {
      await eth.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: BASE_SEPOLIA.hexChainId,
            chainName: 'Base Sepolia',
            nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            rpcUrls: [BASE_SEPOLIA.rpcUrl],
            blockExplorerUrls: [BASE_SEPOLIA.explorerUrl],
          },
        ],
      });
    } else {
      throw switchError;
    }
  }
  const browserProvider = new ethers.BrowserProvider(eth);
  return browserProvider.getSigner();
};

export const getSigner = async (
  txCost: string,
  functionName: string,
): Promise<ethers.Signer | null> => {
  const { approveSigner } = useSignerStore.getState();

  const signerAccepted = await approveSigner(txCost, functionName);

  if (signerAccepted) {
    // The stored signer is already bound to the injected BrowserProvider
    return getState().wallet ?? null;
  } else {
    throw new UserRejectedSigningError();
  }
};
